import moment from 'moment';
import { getVertexId } from '../../../core/util';
import type { ConnectedTypes } from '../../make-connected';
import type { Types } from '../../types';
import { range } from '../../../common/functions';
import type { Interval } from '../../../core/interfaces';

type Range = Partial<Interval<string | moment.Moment | Date>>;
type DateRange = Interval<moment.Moment>;

function createRange ({ start, end }: Range): DateRange {
  return {
    start: moment.utc(start).clone().startOf('isoWeek'),
    end:   moment.utc(end  ).clone().endOf  ('isoWeek')
  };
}

export function parsePeriods (
  periods:  ConnectedTypes.period[],
  division: ConnectedTypes.division,
  options:  Types.parsedToOptions
): Types.Entities.Period[] {
  const divisionRange = createRange(division);

  // build a mapping of week starts to boolean active flags
  const weeksMap = new Map<string, boolean>();
  for (const x = divisionRange.start.clone(); x.isSameOrBefore(divisionRange.end); x.add(1, 'week')) {
    weeksMap.set(x.toISOString(), false);
  }

  const out = periods.map((p): Types.Entities.Period => {
    const id = getVertexId(p, options);

    // preserve only the ranges that lay within the division range
    const clippedRanges = p.ranges
      .map(r => createRange(r))
      .filter(r => range.overlaps(r, divisionRange))
      .map(r => ({
        start: moment.max(r.start, divisionRange.start).clone(),
        end:   moment.min(r.end,   divisionRange.end  ).clone()
      }));

    // get the active weeks by checking which weeks in the division are covered by the clipped ranges
    const activeWeeksMap = new Map(weeksMap);
    clippedRanges.forEach(r => {
      for (const x = r.start.clone(); x.isSameOrBefore(r.end); x.add(1, 'week')) {
        const key = x.toISOString();
        activeWeeksMap.set(key, true);
      }
    });

    // build the active weeks string
    const weeks = Array.from(activeWeeksMap.entries())
      .sort((a, b) => moment.utc(a[0]).diff(moment.utc(b[0])))
      .map(x => x[1] ? '1' : '0')
      .join('');

    const overlaps = weeks.length <= 64 ? weeks : '';

    return { id, weeks, overlaps };
  });

  // If there are more than 64 weeks but less than or equal to 64 periods, we compute the overlap between periods directly.
  if (weeksMap.size > 64 && out && out.length <= 64) {
    for (let i = 0; i < out.length; i++) {
      let overlaps = '';
      for (let j = 0; j < out.length; j++) {
        // self-overlap
        if (i == j) {
          overlaps += '1';
          continue;
        }

        // overlap with other periods
        let overlap = false;
        for (let k = 0; k < out[i].weeks.length; k++) {
          if (out[i].weeks[k] === '1' && out[j].weeks[k] === '1') {
            overlap = true;
            break;
          }
        }
        overlaps += overlap ? '1' : '0';
      }
      out[i].overlaps = overlaps;
    }
  }

  return out;
}